import {
  calculateCenterCrop,
  convertImageToJpeg,
  ImageColorSpace,
  ImageCrop,
} from './imageConversion';

export const CAROUSEL_MIN_ITEMS = 2;
export const CAROUSEL_MAX_ITEMS = 10;
export const CAROUSEL_MAX_IMAGE_BYTES = 8 * 1024 * 1024;
export const CAROUSEL_MAX_VIDEO_BYTES = 1024 * 1024 * 1024;
export const BYTES_PER_MEGABYTE = 1024 * 1024;

export type FeedPublicationFormat = 'image' | 'reels' | 'carousel' | null;

export const inferFeedPublicationFormat = (
  items: ReadonlyArray<{ fileType: 'image' | 'video' }>,
): FeedPublicationFormat => {
  if (items.length >= CAROUSEL_MIN_ITEMS) return 'carousel';
  if (items.length === 0) return null;
  return items[0].fileType === 'video' ? 'reels' : 'image';
};
export const CAROUSEL_MAX_IMAGE_WIDTH = 1440;
export const CAROUSEL_MIN_IMAGE_WIDTH = 320;
export const CAROUSEL_MIN_ASPECT_RATIO = 4 / 5;
export const CAROUSEL_MAX_ASPECT_RATIO = 1.91;

export interface CarouselImageResult {
  file: File;
  width: number;
  height: number;
  colorSpace: ImageColorSpace;
  crop: ImageCrop;
}

const readImageDimensions = (file: File): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const imageUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(imageUrl);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error('The selected image could not be decoded'));
    };
    image.src = imageUrl;
  });

export const prepareCarouselImage = async (file: File): Promise<CarouselImageResult> => {
  const original = await readImageDimensions(file);
  const crop = calculateCenterCrop(original, {
    minimum: CAROUSEL_MIN_ASPECT_RATIO,
    maximum: CAROUSEL_MAX_ASPECT_RATIO,
  });
  const width = Math.min(CAROUSEL_MAX_IMAGE_WIDTH, Math.max(CAROUSEL_MIN_IMAGE_WIDTH, crop.width));
  const height = Math.max(1, Math.round(width * crop.height / crop.width));
  const converted = await convertImageToJpeg(file, { width, height }, crop);

  return { ...converted, crop };
};

export const isSupportedCarouselVideo = (file: File): boolean => {
  const type = file.type.toLowerCase();
  return type === 'video/mp4' || type === 'video/quicktime' || /\.(mp4|mov)$/i.test(file.name);
};
